import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { api } from '../services/api';
import ReceiptUpload, { looksLikePdf, MAX_FILES } from '../components/ReceiptUpload';
import ReceiptTemplatesManager from '../components/ReceiptTemplatesManager';
import { MAX_DOCUMENT_SIZE_MB } from '../constants/upload';
import downloadBlob from '../utils/downloadBlob';
import './ReceiptsPage.css';

const DOCX_MIME = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

function b64ToBlob(b64, type) {
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return new Blob([bytes], { type });
}

function WarningModal({ open, badFiles, onCancel, onConfirm }) {
  const { t } = useTranslation('receipts');
  if (!open) return null;
  return (
    <div className="receipts-modal-overlay" onClick={onCancel}>
      <div className="receipts-modal" onClick={(e) => e.stopPropagation()}>
        <h2>{t('warningModal.title')}</h2>
        <div className="receipts-modal-body">
          <p>{t('warningModal.body')}</p>
          <ul className="receipts-warn-list">
            {badFiles.map((f, i) => (
              <li key={i}>• {f.name}</li>
            ))}
          </ul>
        </div>
        <div className="receipts-modal-footer">
          <button type="button" className="btn btn-ghost" onClick={onCancel}>
            {t('warningModal.cancel')}
          </button>
          <button type="button" className="btn btn-primary" onClick={onConfirm}>
            {t('warningModal.confirm')}
          </button>
        </div>
      </div>
    </div>
  );
}

function ProcessTab({ workspaceId, templates }) {
  const { t } = useTranslation('receipts');
  const [templateId, setTemplateId] = useState('');
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [warnOpen, setWarnOpen] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!templateId && templates && templates.length > 0) {
      setTemplateId(templates[0].id);
    }
  }, [templates, templateId]);

  const startProcess = async () => {
    setError(null);
    setResult(null);
    if (!templateId) {
      setError(t('errors.noTemplate'));
      return;
    }
    if (files.length === 0) {
      setError(t('errors.noFiles'));
      return;
    }

    const bad = files.filter((f) => !looksLikePdf(f));
    if (bad.length > 0) {
      setWarnOpen(true);
      return;
    }
    await runBatch();
  };

  const runBatch = async () => {
    setWarnOpen(false);
    setBusy(true);
    setError(null);
    try {
      const response = await api.processReceiptBatch(workspaceId, templateId, files);
      for (const f of response.files) {
        const blob = b64ToBlob(f.content_b64, DOCX_MIME);
        downloadBlob(blob, f.filename);
        await new Promise((r) => setTimeout(r, 60));
      }
      setResult(response);
      setFiles([]);
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || 'Unknown error';
      setError(t('errors.batchFailed', {
        message: typeof msg === 'string' ? msg : JSON.stringify(msg),
      }));
    } finally {
      setBusy(false);
    }
  };

  const noTemplates = templates && templates.length === 0;

  return (
    <section className="receipts-section">
      <div className="receipts-field">
        <label>{t('process.templateLabel')}</label>
        {noTemplates ? (
          <div className="receipts-empty-hint">{t('process.noTemplates')}</div>
        ) : (
          <select
            value={templateId}
            onChange={(e) => setTemplateId(e.target.value)}
            disabled={busy || !templates}
          >
            <option value="">{t('process.templatePlaceholder')}</option>
            {(templates || []).map((tpl) => (
              <option key={tpl.id} value={tpl.id}>{tpl.name}</option>
            ))}
          </select>
        )}
      </div>

      <ReceiptUpload files={files} onFilesChange={setFiles} disabled={busy} />

      {files.length > MAX_FILES && (
        <div className="receipts-empty-hint">
          {t('process.tooManyFiles', { max: MAX_FILES })}
        </div>
      )}

      {error && <div className="receipts-empty-hint">{error}</div>}

      <div className="receipts-actions">
        <button
          type="button"
          className="btn btn-primary"
          onClick={startProcess}
          disabled={busy || files.length === 0 || !templateId}
        >
          {busy ? t('process.processingButton') : t('process.processButton', { count: files.length })}
        </button>
      </div>

      {result && (
        <div className="receipts-success">
          {t('process.successCount', { count: result.files.length })}
          {result.skipped.length > 0 && (
            <details className="receipts-skipped" style={{ marginTop: 8 }}>
              <summary>
                {t('process.skippedCount', { count: result.skipped.length })}
              </summary>
              <ul>
                {result.skipped.map((s, i) => (
                  <li key={i}>{s.filename} - {s.reason}</li>
                ))}
              </ul>
            </details>
          )}
        </div>
      )}

      <WarningModal
        open={warnOpen}
        badFiles={files.filter((f) => !looksLikePdf(f))}
        onCancel={() => setWarnOpen(false)}
        onConfirm={runBatch}
      />
    </section>
  );
}

function ReceiptsPage() {
  const { t } = useTranslation('receipts');
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('process');
  const [templates, setTemplates] = useState(null);

  const workspaceId = user?.workspace_id;

  useEffect(() => {
    if (!workspaceId) return;
    api.listReceiptTemplates(workspaceId).then(setTemplates);
  }, [workspaceId]);

  if (!workspaceId) return null;

  return (
    <div className="receipts-page">
      <header className="receipts-header">
        <h1>{t('page.title')}</h1>
        <p>{t('page.subtitle')} (≤ {MAX_FILES} files, ≤ {MAX_DOCUMENT_SIZE_MB} MB each)</p>
      </header>

      <div className="receipts-tabs" role="tablist">
        <button
          className={`receipts-tab ${activeTab === 'process' ? 'active' : ''}`}
          onClick={() => setActiveTab('process')}
        >
          {t('tabs.process')}
        </button>
        <button
          className={`receipts-tab ${activeTab === 'templates' ? 'active' : ''}`}
          onClick={() => setActiveTab('templates')}
        >
          {t('tabs.templates')}
        </button>
      </div>

      {activeTab === 'process' && (
        <ProcessTab workspaceId={workspaceId} templates={templates} />
      )}

      {activeTab === 'templates' && (
        <ReceiptTemplatesManager
          workspaceId={workspaceId}
          onChange={setTemplates}
        />
      )}
    </div>
  );
}

export default ReceiptsPage;
